import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, Spinner, Modal } from 'react-bootstrap';
import { CopyButton } from '../../components/common/Copyable';
import {
  getIntegrationsSendgrid,
  postIntegrationsSendgrid,
  putIntegrationsSendgridIntegrationId,
  deleteIntegrationsSendgridIntegrationId,
  postIntegrationsSendgridIntegrationIdTestConnection,
  postIntegrationsSendgridIntegrationIdSync,
} from '../../api/generated/arrayscapeGamingDashboardAPI';

interface SyncResult {
  integrationId: string;
  message: string;
  sendersSynced?: number;
}

const SendGridTab: React.FC = () => {
  const [sendGridIntegrations, setSendGridIntegrations] = useState<any[]>([]);
  const [isLoadingIntegrations, setIsLoadingIntegrations] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState<any | null>(null);

  // Form fields
  const [formName, setFormName] = useState('');
  const [formApiKey, setFormApiKey] = useState('');
  const [formWebhookKey, setFormWebhookKey] = useState('');
  const [formWebhookEnabled, setFormWebhookEnabled] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);

  // Loading states
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState<string | null>(null);

  // Sync results
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);

  // Fetch integrations on mount
  useEffect(() => {
    fetchIntegrations();
  }, []);

  const fetchIntegrations = async () => {
    setIsLoadingIntegrations(true);
    setError(null);
    try {
      const response = await getIntegrationsSendgrid();
      setSendGridIntegrations(response.integrations || []);
    } catch (error) {
      console.error('Error fetching SendGrid integrations:', error);
      setError('Failed to fetch SendGrid integrations');
    } finally {
      setIsLoadingIntegrations(false);
    }
  };

  const resetForm = () => {
    setFormName('');
    setFormApiKey('');
    setFormWebhookKey('');
    setFormWebhookEnabled(false);
    setShowApiKey(false);
    setError(null);
  };

  const handleOpenAddModal = () => {
    resetForm();
    setShowAddModal(true);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    resetForm();
  };

  const handleOpenEditModal = (integration: any) => {
    setEditingIntegration(integration);
    setFormName(integration.name || '');
    setFormApiKey(''); // Don't populate - user must re-enter to change
    setFormWebhookKey(integration.webhook_verification_key || '');
    setFormWebhookEnabled(integration.webhook_enabled || false);
    setShowApiKey(false);
    setError(null);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingIntegration(null);
    resetForm();
  };

  const handleCreateIntegration = async () => {
    setIsSaving(true);
    setError(null);
    try {
      if (!formName.trim()) {
        setError('Please enter a name for this integration');
        setIsSaving(false);
        return;
      }
      if (!formApiKey.trim()) {
        setError('Please enter an API key');
        setIsSaving(false);
        return;
      }

      await postIntegrationsSendgrid({
        name: formName,
        api_key: formApiKey,
      });

      handleCloseAddModal();
      await fetchIntegrations();
    } catch (error: any) {
      console.error('Error creating SendGrid integration:', error);
      if (error.response?.status === 400) {
        setError('Invalid API key or name already exists');
      } else {
        setError('Failed to create SendGrid integration');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateIntegration = async () => {
    if (!editingIntegration) return;

    setIsSaving(true);
    setError(null);
    try {
      if (!formName.trim()) {
        setError('Please enter a name for this integration');
        setIsSaving(false);
        return;
      }

      const updateData: any = {
        name: formName,
        webhook_verification_key: formWebhookKey || null,
        webhook_enabled: formWebhookEnabled,
      };

      // Only include API key if user entered a new one
      if (formApiKey.trim()) {
        updateData.api_key = formApiKey;
      }

      // Validate: webhook can only be enabled if verification key is provided
      if (formWebhookEnabled && !formWebhookKey.trim()) {
        setError('Webhook verification key is required to enable the webhook');
        setIsSaving(false);
        return;
      }

      await putIntegrationsSendgridIntegrationId(editingIntegration.id, updateData);

      handleCloseEditModal();
      await fetchIntegrations();
    } catch (error: any) {
      console.error('Error updating SendGrid integration:', error);
      if (error.response?.status === 400 && error.response?.data?.message) {
        setError(error.response.data.message);
      } else if (error.response?.status === 400) {
        setError('Invalid request');
      } else {
        setError('Failed to update SendGrid integration');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteIntegration = async (integrationId: string, name: string) => {
    if (!window.confirm(`Are you sure you want to delete the SendGrid integration "${name}"?`)) {
      return;
    }

    try {
      await deleteIntegrationsSendgridIntegrationId(integrationId);
      await fetchIntegrations();
    } catch (error) {
      console.error('Error deleting SendGrid integration:', error);
      setError('Failed to delete SendGrid integration');
    }
  };

  const handleTestConnection = async (integrationId: string) => {
    setIsTesting(integrationId);
    try {
      await postIntegrationsSendgridIntegrationIdTestConnection(integrationId);
      await fetchIntegrations();
    } catch (error: any) {
      console.error('Error testing SendGrid connection:', error);
      await fetchIntegrations();
    } finally {
      setIsTesting(null);
    }
  };

  const handleSyncIntegration = async (integrationId: string) => {
    setIsSyncing(integrationId);
    setSyncResult(null);
    try {
      const response = await postIntegrationsSendgridIntegrationIdSync(integrationId);
      setSyncResult({
        integrationId,
        message: response.message || 'Sync completed',
        sendersSynced: response.senders_synced,
      });
      await fetchIntegrations();
    } catch (error: any) {
      console.error('Error syncing SendGrid data:', error);
      setError('Failed to sync SendGrid data');
      await fetchIntegrations();
    } finally {
      setIsSyncing(null);
    }
  };


  return (
    <>
      <Card className="">
        <Card.Body>
          <div className="d-flex align-items-center justify-content-between mb-4">
            <div className="d-flex align-items-center">
              <div className="ms-4 me-4 bg-header-control rounded p-2">
                <img
                  src="/sendgrid-logo.png"
                  alt="SendGrid"
                  style={{ height: '3rem' }}
                />
              </div>
              <div>
                <h5 className="mb-1">SendGrid Integrations</h5>
                <p className="text-muted mb-0">Connect your SendGrid account for email marketing campaigns</p>
              </div>
            </div>
            <Button
              variant="primary"
              onClick={handleOpenAddModal}
              disabled={isSaving}
            >
              <i className="bi bi-plus-circle me-1"></i>
              Add Integration
            </Button>
          </div>

          {error && !showAddModal && !showEditModal && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {syncResult && (
            <Alert variant="success" dismissible onClose={() => setSyncResult(null)}>
              <strong>Sync completed:</strong> {syncResult.sendersSynced || 0} senders synced
            </Alert>
          )}

          {isLoadingIntegrations ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <p className="mt-2 text-muted">Loading integrations...</p>
            </div>
          ) : sendGridIntegrations.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-envelope-at display-4 text-muted"></i>
              <h6 className="mt-3 text-muted">No SendGrid Integrations</h6>
              <p className="text-muted">Add your first SendGrid integration to enable email marketing</p>
            </div>
          ) : (
            <div className="row">
              {sendGridIntegrations.map((integration) => (
                <div key={integration.id} className="col-md-6 mb-3">
                  <Card className="border">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div className="flex-grow-1">
                          <h6 className="mb-1">
                            {integration.name || 'SendGrid Account'}
                          </h6>
                          <div className="text-muted small mb-1">
                            <i className="bi bi-key me-1"></i>
                            API Key configured
                          </div>
                          {integration.webhook_enabled ? (
                            <div className="text-success small">
                              <i className="bi bi-broadcast me-1"></i>
                              Event webhook enabled
                            </div>
                          ) : (
                            <div className="text-muted small">
                              <i className="bi bi-broadcast me-1"></i>
                              Event webhook disabled
                            </div>
                          )}
                        </div>
                        <span className={`badge ${
                          integration.connection_status === 'connected' ? 'bg-success' :
                          integration.connection_status === 'error' ? 'bg-danger' : 'bg-secondary'
                        }`}>
                          {integration.connection_status === 'connected' ? 'Connected' :
                           integration.connection_status === 'error' ? 'Error' : 'Not Tested'}
                        </span>
                      </div>

                      <div className="d-flex gap-2 mb-3">
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() => handleTestConnection(integration.id)}
                          disabled={isTesting === integration.id}
                        >
                          {isTesting === integration.id ? (
                            <Spinner animation="border" size="sm" className="me-1" />
                          ) : (
                            <i className="bi bi-check-circle me-1"></i>
                          )}
                          Test
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleSyncIntegration(integration.id)}
                          disabled={isSyncing === integration.id}
                        >
                          {isSyncing === integration.id ? (
                            <Spinner animation="border" size="sm" className="me-1" />
                          ) : (
                            <i className="bi bi-arrow-repeat me-1"></i>
                          )}
                          Sync
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => handleOpenEditModal(integration)}
                        >
                          <i className="bi bi-pencil me-1"></i>
                          Edit
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteIntegration(integration.id, integration.name)}
                        >
                          <i className="bi bi-trash me-1"></i>
                          Delete
                        </Button>
                      </div>

                      {integration.connection_message && (
                        <div className={`alert ${integration.connection_status === 'connected' ? 'alert-success' : 'alert-danger'} alert-sm mb-3 py-2`} role="alert">
                          <i className={`bi ${integration.connection_status === 'connected' ? 'bi-check-circle' : 'bi-exclamation-triangle'} me-1`}></i>
                          <small>{integration.connection_message}</small>
                        </div>
                      )}

                      <div className="small text-muted pt-2 border-top">
                        <div className="d-flex justify-content-between">
                          <span>Last Sync:</span>
                          <span>{integration.last_sync_at ? new Date(integration.last_sync_at).toLocaleString() : 'Never'}</span>
                        </div>
                        {integration.last_test_at && (
                          <div className="d-flex justify-content-between">
                            <span>Last Test:</span>
                            <span>{new Date(integration.last_test_at).toLocaleString()}</span>
                          </div>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Add SendGrid Integration Modal */}
      <Modal show={showAddModal} onHide={handleCloseAddModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add SendGrid Integration</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Form.Group className="mb-3">
            <Form.Label>Integration Name <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g., Production SendGrid"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              maxLength={255}
            />
            <Form.Text className="text-muted">
              Friendly name to identify this integration
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>API Key <span className="text-danger">*</span></Form.Label>
            <div className="input-group">
              <Form.Control
                type={showApiKey ? "text" : "password"}
                placeholder="SG.xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                value={formApiKey}
                onChange={(e) => setFormApiKey(e.target.value)}
              />
              <Button
                variant="outline-secondary"
                onClick={() => setShowApiKey(!showApiKey)}
              >
                <i className={`bi ${showApiKey ? 'bi-eye-slash' : 'bi-eye'}`}></i>
              </Button>
            </div>
            <Form.Text className="text-muted">
              <i className="bi bi-shield-lock me-1"></i>
              Your API key will be encrypted and stored securely.{' '}
              <a href="https://app.sendgrid.com/settings/api_keys" target="_blank" rel="noopener noreferrer">
                Create API key
              </a>
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleCloseAddModal}>
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleCreateIntegration}
            disabled={isSaving || !formName.trim() || !formApiKey.trim()}
          >
            {isSaving ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Creating...
              </>
            ) : (
              <>
                <i className="bi bi-plus-circle me-1"></i>
                Create Integration
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit SendGrid Integration Modal */}
      <Modal show={showEditModal} onHide={handleCloseEditModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit SendGrid Integration</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Integration Name <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g., Production SendGrid"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              maxLength={255}
            />
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label>API Key</Form.Label>
            <div className="input-group">
              <Form.Control
                type={showApiKey ? "text" : "password"}
                placeholder="Leave blank to keep existing key"
                value={formApiKey}
                onChange={(e) => setFormApiKey(e.target.value)}
              />
              <Button
                variant="outline-secondary"
                onClick={() => setShowApiKey(!showApiKey)}
              >
                <i className={`bi ${showApiKey ? 'bi-eye-slash' : 'bi-eye'}`}></i>
              </Button>
            </div>
            <Form.Text className="text-muted">
              Only enter a new API key if you want to change it
            </Form.Text>
          </Form.Group>

          <hr />

          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="webhook-enabled-switch"
              label={<><i className="bi bi-broadcast me-2"></i><strong>Enable Event Webhook</strong></>}
              checked={formWebhookEnabled}
              onChange={(e) => setFormWebhookEnabled(e.target.checked)}
            />
            <Form.Text className="text-muted">
              When enabled, SendGrid webhook calls will be processed. Otherwise, 401 Unauthorized will be returned.
            </Form.Text>
          </Form.Group>

          {formWebhookEnabled && (
          <>
          <p className="text-muted small mb-3">
            Configure SendGrid to send notifications about delivery events.
          </p>

          <Card className="mb-3 bg-body-tertiary">
            <Card.Body className="py-2">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <strong className="small">SendGrid Webhook Configuration</strong>
                <a
                  href="https://app.sendgrid.com/settings/mail_settings/webhook_settings"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-primary btn-sm"
                >
                  <i className="bi bi-box-arrow-up-right me-1"></i>
                  Open SendGrid
                </a>
              </div>
              <div className="small">
                <div className="mb-2">
                  <strong>Post URL:</strong>
                  <div className="input-group input-group-sm mt-1">
                    <Form.Control
                      type="text"
                      readOnly
                      value={`${window.location.origin}/api/v1/sendgrid/webhook`}
                      style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                    />
                    <CopyButton
                      value={`${window.location.origin}/api/v1/sendgrid/webhook`}
                      variant="outline-secondary"
                      size="sm"
                    />
                  </div>
                </div>
                <div>
                  <strong>Recommended actions:</strong> Opened, Clicked, Unsubscribed, Spam Reports, Group Unsubscribes, Group Resubscribes, Processed, Dropped, Deferred, Bounced, Delivered
                </div>
              </div>
            </Card.Body>
          </Card>

          <Form.Group className="mb-3">
            <Form.Label>
              Webhook Verification Key <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Paste verification key from SendGrid (enable 'Signed Event Webhook' in SendGrid settings)"
              value={formWebhookKey}
              onChange={(e) => setFormWebhookKey(e.target.value)}
              style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
            />
            <Form.Text className="text-muted">
              Enable "Signed Event Webhook" in SendGrid security settings and paste the verification key here
            </Form.Text>
          </Form.Group>
          </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleCloseEditModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdateIntegration}
            disabled={isSaving || !formName.trim()}
          >
            {isSaving ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Saving...
              </>
            ) : (
              <>
                <i className="bi bi-check-lg me-1"></i>
                Save Changes
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

    </>
  );
};

export default SendGridTab;
